'use client';

import type { Message } from '@/i18n';
import { TapLink, tapTo } from './tap-link';
import { useDismissTapMount } from './tap-mount-store';
import { useRouter } from 'next/navigation';
import type { Glyph } from '@/domain/tags';
import { GlyphTip } from '@/components/values/glyph-tip';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Menu } from '@/components/ui/menu';
import { CaretLeft, CaretRight, House, Kanban, MagnifyingGlass } from '@/components/icons';
import { hasInAppHistory } from '@/lib/nav-history';
import { useT } from '@/lib/locale-context';
import styles from './path-trail.module.css';

export interface RootCrumb {
  href: string;
  /** A board's or a group's name as it is, or a descriptor. */
  label: string | Message;
  kind: 'home' | 'group' | 'search';
}
export interface ItemCrumb {
  id: string;
  title: string;
  /** The two-axis glyph (MACHINE.md §1) for the crumb dot. Absent where the
   *  viewer cannot resolve one (a border stub); the crumb then carries its
   *  title without a dot rather than a made-up one. */
  glyph?: Glyph;
  href: string;
}

// How many leading item-crumbs to collapse behind a "…" menu once the trail
// grows (wide viewports). The root and the last two items always show; the
// rest stay reachable in the menu. On compact EVERY intermediate crumb
// collapses - a label-less dot crumb said nothing (Leon, 2026-07-11), so the
// phone shows root › … and keeps the titles one tap away. (A width-measured
// "as much as fits" is a later refinement.)
//
// The trail names the way HERE and stops: the item itself is not a crumb
// (Leon, 2026-09-15 - the title row already says it, so a current crumb
// repeated the name and gave the bar nothing). The last crumb is the step
// you came from.

const VISIBLE_TAIL = 2;

function ViewIcon({ kind }: { kind: RootCrumb['kind'] }) {
  if (kind === 'home') return <House className={styles.viewIcon} aria-hidden />;
  if (kind === 'search') return <MagnifyingGlass className={styles.viewIcon} aria-hidden />;
  return <Kanban className={styles.viewIcon} aria-hidden />;
}

/** The overflow: hidden steps as a kit Menu (anchored popover on desktop,
 *  bottom sheet on compact - replaces the old hand-rolled absolute dropdown,
 *  per the ui-kit local-absolute rule). Each entry keeps its status dot and
 *  full title.
 *
 *  **The trigger COUNTS, it does not say "…"** (Leon, 2026-09-20). It wore
 *  the three dots, and so does the dock's item-meta sheet - one glyph, two
 *  unrelated meanings, and the phone test read neither. "2 more" says what is
 *  behind it; the dots belong to the meta register alone. */
function CrumbOverflow({
  root,
  crumbs,
  className,
  trailingSep = true,
}: {
  /** The trail's root: each hidden step's own trail starts from it. */
  root?: RootCrumb;
  /** The hidden steps: the trail's leading items, in order. */
  crumbs: ItemCrumb[];
  className?: string;
  /** The wide overflow leads on to named crumbs; the compact one is the trail's
   *  last step, so it carries no separator. */
  trailingSep?: boolean;
}) {
  const t = useT();
  const router = useRouter();
  return (
    <span className={[styles.overflow, className].filter(Boolean).join(' ')}>
      <Menu
        ariaLabel={t('shell.trail.hiddenSteps')}
        align="start"
        items={crumbs.map((c, i) => ({
          label: c.title,
          icon: c.glyph ? (
            <span
              className={styles.itemDot}
              data-health={c.glyph.health}
              data-tense={c.glyph.tense}
              aria-hidden
            />
          ) : undefined,
          onSelect: () => {
            tapTo(c.href, { title: c.title, trail: { root, items: crumbs.slice(0, i) } });
            router.push(c.href);
          },
        }))}
        trigger={({ open, toggle }) => (
          <Button
            variant="ghost"
            size="sm"
            className={styles.more}
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={open}
            title={t('shell.trail.showHidden')}
          >
            {t('shell.trail.more', { count: crumbs.length })}
          </Button>
        )}
      />
      {trailingSep && <CaretRight className={styles.sep} aria-hidden />}
    </span>
  );
}

export function PathTrail({
  root,
  items,
  parents = [],
}: {
  root?: RootCrumb;
  items: ItemCrumb[];
  /** The item's own parents, named on a direct visit (docs/CHECKLIST.md §2,
   *  2026-11-03): with no trail to read, the bar names where the item sits,
   *  both of two parents, joined as a set rather than chained as a path.
   *  Drawn only while `items` is empty, at every width. */
  parents?: ItemCrumb[];
}) {
  const t = useT();
  const rootLabel = root ? (typeof root.label === 'string' ? root.label : t.msg(root.label)) : '';
  const router = useRouter();

  const collapsed = items.length > VISIBLE_TAIL + 1;
  const hidden = collapsed ? items.slice(0, items.length - VISIBLE_TAIL) : [];
  const shown = collapsed ? items.slice(items.length - VISIBLE_TAIL) : items;

  // Back pops history when that stays in-app; otherwise (a direct/shared link,
  // or the opening navigation got replaced - see src/lib/nav-history.ts) it
  // walks one crumb up the trail instead of leaving the app, replacing the
  // current entry so the next Back does not pop back down.
  const backFallback = items.at(-1)?.href ?? root?.href ?? '/work';
  const dismissTapMount = useDismissTapMount();
  const back = () => {
    // The trail of a skeleton mounted by a tap: Back dismisses it (history
    // has no entry for its target yet; docs/LOADING.md §2, tool 3).
    if (dismissTapMount()) return;
    if (hasInAppHistory()) router.back();
    else router.replace(backFallback);
  };

  return (
    <nav className={styles.trail} aria-label={t('shell.trail.label')} data-testid="path-trail">
      <IconButton
        className={styles.back}
        onClick={back}
        title={t('shell.trail.back')}
        label={t('shell.trail.backOneStep')}
      >
        <CaretLeft />
      </IconButton>

      {root && (
        <>
          <TapLink
            href={root.href}
            tap={{ title: rootLabel }}
            className={styles.crumb}
            data-role="view"
          >
            <ViewIcon kind={root.kind} />
            <span className={styles.label}>{rootLabel}</span>
          </TapLink>
          {/* A separator only leads somewhere: none after the last crumb. */}
          {(items.length > 0 || parents.length > 0) && (
            <CaretRight className={styles.sep} aria-hidden />
          )}
        </>
      )}
      {items.length === 0 && parents.length > 0 && (
        <span className={styles.parents} data-testid="path-trail-parents">
          {parents.map((p, i) => (
            <span key={p.id} className={styles.parent}>
              {i > 0 && (
                <span className={styles.and} aria-hidden>
                  ·
                </span>
              )}
              <TapLink
                href={p.href}
                tap={{ title: p.title, trail: { root, items: [] } }}
                className={styles.crumb}
                data-role="item"
                data-closed={p.glyph?.closed ? 'true' : undefined}
              >
                {p.glyph && (
                  <span
                    className={styles.itemDot}
                    data-health={p.glyph.health}
                    data-tense={p.glyph.tense}
                    aria-hidden
                  />
                )}
                <span className={styles.label}>{p.title}</span>
              </TapLink>
            </span>
          ))}
        </span>
      )}

      {/* Wide partition: leading crumbs behind "…" once the trail grows, the
          tail as named crumbs. */}
      {collapsed && <CrumbOverflow root={root} crumbs={hidden} className={styles.overflowWide} />}
      {shown.map((it, i) => (
        <span key={it.id} className={styles.step}>
          <TapLink
            href={it.href}
            tap={{ title: it.title, trail: { root, items: items.slice(0, i + hidden.length) } }}
            className={styles.crumb}
            data-role="item"
            data-closed={it.glyph?.closed ? 'true' : undefined}
          >
            {it.glyph && (
              <GlyphTip states={it.glyph} placement="bottom">
                <span
                  className={styles.itemDot}
                  data-health={it.glyph.health}
                  data-tense={it.glyph.tense}
                />
              </GlyphTip>
            )}
            <span className={styles.label}>{it.title}</span>
          </TapLink>
          {i < shown.length - 1 && <CaretRight className={styles.sep} aria-hidden />}
        </span>
      ))}
      {/* Compact partition: ALL intermediate crumbs behind one "…". Both
          partitions render and CSS gates them (structure switches in CSS -
          the server render cannot know the viewport). */}
      {items.length > 0 && (
        <CrumbOverflow
          root={root}
          crumbs={items}
          className={styles.overflowCompact}
          trailingSep={false}
        />
      )}
    </nav>
  );
}
